import React, { useEffect, useRef, useState } from "react";

/**
 * Cinematic HUD overlay — timecode + REC + framing marks.
 * Sits above a dark hero image; text is white with subtle glow.
 */
export default function HeroHUD() {
  const [tc, setTc] = useState({ h: 14, m: 32, s: 7, f: 12 });

  useEffect(() => {
    // 24fps-style rolling timecode; increments frame every ~42ms
    const id = setInterval(() => {
      setTc((prev) => {
        let { h, m, s, f } = prev;
        f += 1;
        if (f >= 24) {
          f = 0;
          s += 1;
          if (s >= 60) {
            s = 0;
            m += 1;
            if (m >= 60) {
              m = 0;
              h = (h + 1) % 24;
            }
          }
        }
        return { h, m, s, f };
      });
    }, 42);
    return () => clearInterval(id);
  }, []);

  const pad = (n) => String(n).padStart(2, "0");
  const timecode = `${pad(tc.h)}:${pad(tc.m)}:${pad(tc.s)}:${pad(tc.f)}`;

  return (
    <>
      {/* Framing marks */}
      <div className="frame-mark tl" data-testid="frame-tl" />
      <div className="frame-mark tr" data-testid="frame-tr" />
      <div className="frame-mark bl" data-testid="frame-bl" />
      <div className="frame-mark br" data-testid="frame-br" />

      {/* Top-right HUD */}
      <div
        data-testid="hero-hud"
        className="absolute top-24 right-8 md:right-12 flex items-center gap-3 mono text-[11px] tracking-[0.18em] text-[#F9F8F6]/90 select-none pointer-events-none"
      >
        <span className="rec-dot" aria-hidden="true" />
        <span className="uppercase">Rec</span>
        <span className="text-[#F9F8F6]/40">·</span>
        <span data-testid="hero-timecode" className="tabular-nums">
          TC {timecode}
        </span>
        <span className="text-[#F9F8F6]/40">·</span>
        <span>SP · 2026</span>
      </div>

      {/* Bottom-left slate */}
      <div
        data-testid="hero-slate"
        className="absolute bottom-24 md:bottom-32 right-8 md:right-12 hidden md:flex items-center gap-3 mono text-[10px] tracking-[0.24em] text-[#F9F8F6]/70 select-none pointer-events-none uppercase"
      >
        <span>Cena 00</span>
        <span className="text-[#F9F8F6]/30">/</span>
        <span>Take 01</span>
        <span className="text-[#F9F8F6]/30">/</span>
        <span>Shambala</span>
      </div>
    </>
  );
}

/**
 * Horizontal marquee ticker running Alta-red words related to content production.
 * Duplicated content so the CSS animation loops seamlessly.
 */
export function ProductionMarquee({ testid = "marquee" }) {
  const items = [
    "Editorial",
    "Golden Hour",
    "Behind the Scenes",
    "Reels",
    "Retratos",
    "Cover",
    "Fireside",
    "Poolside",
    "Creator Day",
    "Sunset Rooftop",
    "Fashion Set",
    "Green Screen",
    "Slow Motion",
    "Direct to Camera",
    "Voiceover",
  ];
  const Row = () => (
    <div className="flex items-center gap-12 pr-12">
      {items.map((t, i) => (
        <span
          key={i}
          className="mono text-[13px] uppercase tracking-[0.28em] text-[color:var(--alta-red)] flex items-center gap-12"
        >
          {t}
          <span className="text-[color:var(--alta-red)]/40">✦</span>
        </span>
      ))}
    </div>
  );
  return (
    <div
      data-testid={testid}
      className="relative overflow-hidden bg-[#F3F1EA] border-y border-neutral-200 py-5"
    >
      <div className="marquee-track">
        <Row />
        <Row />
      </div>
    </div>
  );
}

/**
 * Animated integer that eases from 0 to `value` on first viewport intersection.
 */
export function CountUp({ value, duration = 1600, testid }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const t0 = performance.now();
            const step = (t) => {
              const p = Math.min(1, (t - t0) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setN(Math.round(eased * value));
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref} data-testid={testid} className="tabular-nums">
      {String(n).padStart(2, "0")}
    </span>
  );
}
